import type { SDLCCategory } from '../../types/analysis.types';

export const vibeMapperCategories: SDLCCategory[] = [
  {
    id: 'source-control',
    name: 'Source Control',
    score: 85,
    status: 'detected',
    weight: 0.125,
    findings: [
      {
        id: 'sc1',
        title: 'Git repository with active branching',
        description:
          'Well-maintained GitLab repository with 796 commits in the last 30 days across 9 active contributors.',
        severity: 'low',
      },
      {
        id: 'sc2',
        title: 'Comprehensive .gitignore',
        description:
          '.gitignore covers node_modules, build artifacts, environment files, and IDE configs.',
        severity: 'low',
      },
      {
        id: 'sc3',
        title: 'No branch protection rules detected',
        description:
          'Branch protection for main branch could not be confirmed from repository analysis.',
        severity: 'medium',
      },
    ],
  },
  {
    id: 'build',
    name: 'Build & Integration',
    score: 85,
    status: 'detected',
    weight: 0.125,
    findings: [
      {
        id: 'b1',
        title: 'Multi-project build system',
        description:
          'pnpm workspaces manage multiple sub-projects (web-app, api-server). Python projects use pip and poetry.',
        severity: 'low',
      },
      {
        id: 'b2',
        title: 'Docker multi-stage builds',
        description:
          'Dockerfile and Docker Compose configured with multi-stage builds for server and client.',
        severity: 'low',
      },
      {
        id: 'b3',
        title: 'No automated dependency updates',
        description:
          'Neither Dependabot nor Renovate is configured. 240 of 370 dependencies are outdated (65% drift).',
        severity: 'medium',
      },
    ],
  },
  {
    id: 'testing',
    name: 'Testing & Quality',
    score: 100,
    status: 'detected',
    weight: 0.15,
    findings: [
      {
        id: 't1',
        title: 'E2E tests with Playwright',
        description:
          'End-to-end test suite covers critical user flows with Playwright browser automation.',
        severity: 'low',
      },
      {
        id: 't2',
        title: 'Python tests with pytest',
        description:
          'AI engine component has pytest test suite including container service parity tests.',
        severity: 'low',
      },
      {
        id: 't3',
        title: 'Coverage threshold configured',
        description:
          'Coverage threshold is set but not enforced in CI pipeline - tests pass regardless of coverage.',
        severity: 'medium',
      },
    ],
  },
  {
    id: 'deployment',
    name: 'Release & Deployment',
    score: 100,
    status: 'detected',
    weight: 0.15,
    findings: [
      {
        id: 'd1',
        title: 'Kubernetes deployment via Helm',
        description:
          'Full Kubernetes deployment with Helm charts for dev, staging, and production GKE clusters.',
        severity: 'low',
      },
      {
        id: 'd2',
        title: 'Terraform infrastructure-as-code',
        description:
          'All cloud infrastructure managed by Terraform modules (GKE, networking, observability, databases).',
        severity: 'low',
      },
      {
        id: 'd3',
        title: 'Cloudflare Pages deployment',
        description: 'LiveSite deploys to Cloudflare Pages and Workers via GitLab CI pipeline.',
        severity: 'low',
      },
    ],
  },
  {
    id: 'security',
    name: 'Security & Compliance',
    score: 60,
    status: 'partial',
    weight: 0.15,
    findings: [
      {
        id: 's1',
        title: 'HashiCorp Vault secrets management',
        description:
          'Production secrets managed via Vault with AppRole and JWT/OIDC authentication. Vault Agent renders runtime secrets.',
        severity: 'low',
      },
      {
        id: 's2',
        title: 'No vulnerability scanning',
        description:
          'No dependency vulnerability scanning (Snyk, Trivy, npm audit) in CI pipeline.',
        severity: 'critical',
      },
      {
        id: 's3',
        title: 'No SAST tool configured',
        description:
          'No static application security testing (SonarQube, Semgrep, CodeQL) detected.',
        severity: 'high',
      },
      {
        id: 's4',
        title: 'No secret detection tool',
        description:
          'No Gitleaks, TruffleHog, or similar tool to prevent accidental secret commits.',
        severity: 'high',
      },
    ],
  },
  {
    id: 'collaboration',
    name: 'Collaboration',
    score: 20,
    status: 'partial',
    weight: 0.1,
    findings: [
      {
        id: 'c1',
        title: 'Well-configured CLAUDE.md',
        description:
          'Comprehensive AI agent configuration with project context, commands, and architectural guidance.',
        severity: 'low',
      },
      {
        id: 'c2',
        title: 'No PR template',
        description:
          'Missing merge request template to standardize code review and change descriptions.',
        severity: 'high',
      },
      {
        id: 'c3',
        title: 'No issue templates',
        description: 'No structured templates for bug reports or feature requests.',
        severity: 'medium',
      },
    ],
  },
  {
    id: 'documentation',
    name: 'Documentation',
    score: 100,
    status: 'detected',
    weight: 0.1,
    findings: [
      {
        id: 'doc1',
        title: 'Comprehensive documentation',
        description:
          '28 documentation files covering deployment, security, database management, and product strategy.',
        severity: 'low',
      },
      {
        id: 'doc2',
        title: 'Infrastructure deployment guide',
        description:
          'Detailed guides for deploying to dev, staging, and production Kubernetes clusters.',
        severity: 'low',
      },
      {
        id: 'doc3',
        title: 'CLAUDE.md agent configuration',
        description:
          'Project-level and sub-project CLAUDE.md files provide deep architectural context.',
        severity: 'low',
      },
    ],
  },
  {
    id: 'ai-tooling',
    name: 'AI Tooling',
    score: 40,
    status: 'partial',
    weight: 0.1,
    findings: [
      {
        id: 'ai1',
        title: 'CLAUDE.md for AI assistance',
        description:
          'Well-structured CLAUDE.md with project context, architecture, and development workflows.',
        severity: 'low',
      },
      {
        id: 'ai2',
        title: 'No AI code review integration',
        description: 'No automated AI code review in the merge request pipeline.',
        severity: 'medium',
      },
      {
        id: 'ai3',
        title: 'No AI-assisted PR summaries',
        description: 'Merge requests lack AI-generated summaries or changelogs.',
        severity: 'low',
      },
    ],
  },
];
